import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { api } from '@/App';
import { Eye, EyeOff, Plus, Pencil, Trash2, User, Shield } from 'lucide-react';
import { Textarea } from '@/components/ui/textarea';
import { ROLE_DESCRIPTIONS, isMaster } from '@/lib/permissions';

const SettingsUser = () => {
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  const [roleDescriptions, setRoleDescriptions] = useState({});
  
  // Dialog states
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  
  // Password visibility states
  const [showPassword, setShowPassword] = useState(false);
  
  // Form states
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    role: 'admin_cabang'
  });

  useEffect(() => {
    fetchCurrentUser();
    fetchUsers();
    fetchRoleDescriptions();
  }, []);

  const fetchRoleDescriptions = async () => {
    try {
      const response = await api.get('/users/role-descriptions');
      setRoleDescriptions(response.data || {});
    } catch (error) {
      console.error('Failed to fetch role descriptions:', error);
    }
  };

  const saveRoleDescription = async (role) => {
    try {
      await api.put(`/users/role-descriptions/${role}`, { description: roleDescriptions[role] || '' });
      toast.success('Deskripsi role disimpan');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal menyimpan deskripsi role');
    }
  };

  const fetchCurrentUser = async () => {
    try {
      const response = await api.get('/auth/me');
      setCurrentUser(response.data);
    } catch (error) {
      console.error('Failed to fetch current user:', error);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await api.get('/users');
      setUsers(response.data);
    } catch (error) {
      console.error('Failed to fetch users:', error);
      // Non-admin users may get 403, which is expected
    }
  };

  const resetForm = () => {
    setFormData({
      username: '',
      password: '',
      role: 'admin_cabang'
    });
    setShowPassword(false);
  };

  const handleCreateUser = async () => {
    if (!formData.username.trim() || !formData.password.trim()) {
      toast.error('Username dan password harus diisi');
      return;
    }
    
    if (formData.password.length < 6) {
      toast.error('Password minimal 6 karakter');
      return;
    }

    setLoading(true);
    try {
      await api.post('/users', {
        username: formData.username,
        password: formData.password,
        role: formData.role
      });
      toast.success('User berhasil dibuat');
      setShowCreateDialog(false);
      resetForm();
      fetchUsers();
    } catch (error) {
      console.error('Failed to create user:', error);
      toast.error(error.response?.data?.detail || 'Gagal membuat user');
    } finally {
      setLoading(false);
    }
  };

  const handleEditUser = async () => {
    if (!formData.username.trim()) {
      toast.error('Username tidak boleh kosong');
      return;
    }

    if (formData.password && formData.password.length < 6) {
      toast.error('Password minimal 6 karakter');
      return;
    }

    setLoading(true);
    try {
      const updateData = {
        username: formData.username,
        role: formData.role
      };
      
      // Only include password if provided
      if (formData.password && formData.password.trim()) {
        updateData.password = formData.password;
      }
      
      await api.put(`/users/${selectedUser.id}`, updateData);
      toast.success('User berhasil diperbarui');
      setShowEditDialog(false);
      resetForm();
      fetchUsers();
      
      // Refresh current user if editing self
      if (selectedUser.id === currentUser?.id) {
        fetchCurrentUser();
      }
    } catch (error) {
      console.error('Failed to update user:', error);
      toast.error(error.response?.data?.detail || 'Gagal memperbarui user');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteUser = async () => {
    setLoading(true);
    try {
      await api.delete(`/users/${selectedUser.id}`);
      toast.success('User berhasil dihapus');
      setShowDeleteDialog(false);
      setSelectedUser(null);
      fetchUsers();
    } catch (error) {
      console.error('Failed to delete user:', error);
      toast.error(error.response?.data?.detail || 'Gagal menghapus user');
    } finally {
      setLoading(false);
    }
  };

  const openEditDialog = (user) => {
    setSelectedUser(user);
    setFormData({
      username: user.username,
      password: '',
      role: user.role
    });
    setShowEditDialog(true);
  };

  const openDeleteDialog = (user) => {
    setSelectedUser(user);
    setShowDeleteDialog(true);
  };

  const getRoleBadge = (role) => {
    switch (role) {
      case 'master':
        return <Badge className="bg-amber-100 text-amber-800"><Shield className="w-3 h-3 mr-1" />Master</Badge>;
      case 'master2':
        return <Badge className="bg-orange-100 text-orange-800"><Shield className="w-3 h-3 mr-1" />Master 2</Badge>;
      case 'admin_pusat':
        return <Badge className="bg-purple-100 text-purple-800"><Shield className="w-3 h-3 mr-1" />Admin Pusat</Badge>;
      case 'admin_transaksi_kas_cabang':
        return <Badge className="bg-cyan-100 text-cyan-800"><User className="w-3 h-3 mr-1" />Admin Transaksi & Kas Cabang</Badge>;
      case 'admin_kas_cabang':
        return <Badge className="bg-teal-100 text-teal-800"><User className="w-3 h-3 mr-1" />Admin Kas Cabang</Badge>;
      case 'admin_cabang':
        return <Badge className="bg-blue-100 text-blue-800"><User className="w-3 h-3 mr-1" />Admin Cabang</Badge>;
      case 'investor':
        return <Badge className="bg-green-100 text-green-800"><User className="w-3 h-3 mr-1" />Investor</Badge>;
      default:
        return <Badge variant="outline">{role}</Badge>;
    }
  };

  const isAdmin = isMaster(currentUser);

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Pengaturan User</h1>
          <p className="text-slate-600 mt-2">Kelola pengguna sistem</p>
        </div>
        {isAdmin && (
          <Button 
            onClick={() => { resetForm(); setShowCreateDialog(true); }}
            className="bg-teal-600 hover:bg-teal-700"
          >
            <Plus className="w-4 h-4 mr-2" />
            Tambah User
          </Button>
        )}
      </div>

      {/* Users Table */}
      {isAdmin && (
        <Card>
          <CardHeader>
            <CardTitle>Daftar User</CardTitle>
            <CardDescription>Kelola semua pengguna yang terdaftar</CardDescription>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Username</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Deskripsi Role</TableHead>
                  <TableHead className="text-right">Aksi</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map((user) => (
                  <TableRow key={user.id}>
                    <TableCell className="font-medium">
                      {user.username}
                      {user.id === currentUser?.id && (
                        <Badge variant="outline" className="ml-2 text-xs">Anda</Badge>
                      )}
                    </TableCell>
                    <TableCell>{getRoleBadge(user.role)}</TableCell>
                    <TableCell className="text-xs text-slate-600 max-w-md" data-testid={`role-desc-${user.username}`}>
                      {ROLE_DESCRIPTIONS[user.role] || '-'}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => openEditDialog(user)}
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          onClick={() => openDeleteDialog(user)}
                          disabled={user.id === currentUser?.id}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
                {users.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={4} className="text-center text-slate-400 py-8">
                      Tidak ada user
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* My Profile Section */}
      <Card>
        <CardHeader>
          <CardTitle>Profil Saya</CardTitle>
          <CardDescription>Informasi akun Anda</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Username</Label>
              <p className="text-lg font-medium">{currentUser?.username || '-'}</p>
            </div>
            <div>
              <Label>Role</Label>
              <div className="mt-1">{currentUser && getRoleBadge(currentUser.role)}</div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Create User Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Tambah User Baru</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Username</Label>
              <Input
                type="text"
                value={formData.username}
                onChange={(e) => setFormData({ ...formData, username: e.target.value })}
              />
            </div>
            <div>
              <Label>Password</Label>
              <div className="relative">
                <Input
                  type={showPassword ? "text" : "password"}
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-700"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>
            <div>
              <Label>Role</Label>
              <Select
                value={formData.role}
                onValueChange={(value) => setFormData({ ...formData, role: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="master">Master</SelectItem>
                  <SelectItem value="master2">Master 2</SelectItem>
                  <SelectItem value="admin_pusat">Admin Pusat</SelectItem>
                  <SelectItem value="admin_transaksi_kas_cabang">Admin Transaksi & Kas Cabang</SelectItem>
                  <SelectItem value="admin_kas_cabang">Admin Kas Cabang</SelectItem>
                  <SelectItem value="investor">Investor</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Deskripsi Role</Label>
              <div
                data-testid="role-description-display"
                className="mt-1 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-600"
              >
                {ROLE_DESCRIPTIONS[formData.role] || '-'}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
              Batal
            </Button>
            <Button onClick={handleCreateUser} disabled={loading} className="bg-teal-600 hover:bg-teal-700">
              {loading ? 'Menyimpan...' : 'Simpan'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Edit User</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Username</Label>
              <Input
                type="text"
                value={formData.username}
                onChange={(e) => setFormData({ ...formData, username: e.target.value })}
              />
            </div>
            <div>
              <Label>Password Baru (Kosongkan jika tidak ingin mengubah)</Label>
              <div className="relative">
                <Input
                  type={showPassword ? "text" : "password"}
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  placeholder="Masukkan password baru"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-700"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>
            <div>
              <Label>Role</Label>
              <Select
                value={formData.role}
                onValueChange={(value) => setFormData({ ...formData, role: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="master">Master</SelectItem>
                  <SelectItem value="master2">Master 2</SelectItem>
                  <SelectItem value="admin_pusat">Admin Pusat</SelectItem>
                  <SelectItem value="admin_transaksi_kas_cabang">Admin Transaksi & Kas Cabang</SelectItem>
                  <SelectItem value="admin_kas_cabang">Admin Kas Cabang</SelectItem>
                  <SelectItem value="investor">Investor</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>
              Batal
            </Button>
            <Button onClick={handleEditUser} disabled={loading} className="bg-teal-600 hover:bg-teal-700">
              {loading ? 'Menyimpan...' : 'Simpan'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Hapus User</DialogTitle>
          </DialogHeader>
          <p className="text-slate-600">
            Apakah Anda yakin ingin menghapus user <strong>{selectedUser?.username}</strong>? 
            Aksi ini tidak dapat dibatalkan.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
              Batal
            </Button>
            <Button 
              onClick={handleDeleteUser} 
              disabled={loading}
              className="bg-red-600 hover:bg-red-700"
            >
              {loading ? 'Menghapus...' : 'Hapus'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SettingsUser;
